import React, { useState } from 'react'
import { 
  QrCode, 
  AlertCircle,
  Loader2,
  DollarSign,
  User,
  Mail,
  Phone,
  FileText,
  Calendar,
  CreditCard,
  Check
} from 'lucide-react'
import { pixPopupUtils } from '../utils/popupUtils'

export default function GerarLinkPix() {
  // Estados do formulário
  const [formData, setFormData] = useState({
    ordem_cliente: '',
    codigo_cliente: '',
    nome_cliente: '',
    valor: '',
    cpf_cnpj: '',
    email: '',
    telefone: '',
    descricao: '',
    data_vencimento: ''
  })

  // Estados de UI
  const [loading, setLoading] = useState(false)
  const [erro, setErro] = useState(null)
  const [sucesso, setSucesso] = useState(null)

  /**
   * Atualizar campo do formulário
   */
  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Limpar mensagens de erro ao editar
    if (erro) setErro(null)
  }

  /**
   * Formatar valor para exibição
   */
  const handleValorChange = (e) => {
    const valor = e.target.value.replace(/\D/g, '')
    const valorFormatado = (parseInt(valor || 0) / 100).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
    
    setFormData(prev => ({
      ...prev,
      valor: valorFormatado
    }))
  }

  /**
   * Validar formulário
   */
  const validarFormulario = () => {
    const erros = []

    if (!formData.ordem_cliente) erros.push('Ordem do Cliente é obrigatória')
    if (!formData.codigo_cliente) erros.push('Código do Cliente é obrigatório')
    if (!formData.nome_cliente) erros.push('Nome do Cliente é obrigatório')
    if (!formData.valor || parseFloat(formData.valor.replace('.', '').replace(',', '.')) <= 0) {
      erros.push('Valor deve ser maior que zero')
    }
    if (!formData.cpf_cnpj) erros.push('CPF/CNPJ é obrigatório')

    // Validar formato CPF/CNPJ
    const cpfCnpjLimpo = formData.cpf_cnpj.replace(/\D/g, '')
    if (cpfCnpjLimpo.length !== 11 && cpfCnpjLimpo.length !== 14) {
      erros.push('CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos')
    }

    return erros
  }

  /**
   * Gerar link PIX e abrir nova janela com resultados
   */
  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Validar
    const erros = validarFormulario()
    if (erros.length > 0) {
      setErro(erros.join(', '))
      return
    }

    setLoading(true)
    setErro(null)
    setSucesso(null)

    try {
      // Preparar dados
      const dados = {
        ordem_cliente: parseInt(formData.ordem_cliente),
        codigo_cliente: parseInt(formData.codigo_cliente),
        nome_cliente: formData.nome_cliente,
        valor: parseFloat(formData.valor.replace('.', '').replace(',', '.')),
        cpf_cnpj: formData.cpf_cnpj,
        email: formData.email || undefined,
        telefone: formData.telefone || undefined,
        descricao: formData.descricao || undefined,
        data_vencimento: formData.data_vencimento || undefined
      }

      console.log('📤 Enviando dados:', dados)

      // Chamar API
      const response = await fetch('http://localhost:3001/api/pix/criar', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dados)
      })

      const resultado = await response.json()

      if (!response.ok) {
        throw new Error(resultado.error || 'Erro ao gerar link PIX')
      }

      if (resultado.success) {
        // ⭐ Salvar dados no localStorage para a nova janela
        const dadosParaJanela = {
          ...resultado.data,
          cliente: {
            nome: formData.nome_cliente,
            codigo: formData.codigo_cliente,
            cpf_cnpj: formData.cpf_cnpj
          },
          valor_formatado: formData.valor,
          timestamp: Date.now()
        }
        
        // ⭐ Abrir popup usando utilitário
        const resultadoPopup = await pixPopupUtils.openResultadoAleatorio(dadosParaJanela)
        
        // Mensagem de sucesso baseada no resultado do popup
        let mensagemSucesso = 'Link PIX gerado com sucesso! '
        
        if (resultadoPopup.success) {
          mensagemSucesso += 'Uma nova janela foi aberta com os detalhes. ✅'
        } else if (resultadoPopup.fallbackUsed) {
          mensagemSucesso += 'Popup bloqueado - verifique os resultados na mesma janela. ⚠️'
        } else {
          mensagemSucesso += 'Verifique os resultados. ✅'
        }
        
        setSucesso(mensagemSucesso)
        
        // Limpar formulário após 3 segundos
        setTimeout(() => {
          setFormData({
            ordem_cliente: '',
            codigo_cliente: '',
            nome_cliente: '',
            valor: '',
            cpf_cnpj: '',
            email: '',
            telefone: '',
            descricao: '',
            data_vencimento: ''
          })
          setSucesso(null)
        }, 3000)
      } else {
        throw new Error(resultado.error || 'Erro desconhecido')
      }
    } catch (error) {
      console.error('❌ Erro:', error)
      setErro(error.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl shadow-lg p-8 mb-8 text-white">
        <div className="flex items-center space-x-4">
          <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
            <QrCode className="w-8 h-8" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Gerar Link PIX</h1>
            <p className="text-indigo-100 mt-1">
              Criar link de pagamento aleatório
            </p>
          </div>
        </div>
      </div>

      {/* Alertas */}
      {erro && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-sm font-medium text-red-900">Erro ao gerar link</h4>
            <p className="text-sm text-red-700 mt-1">{erro}</p>
          </div>
        </div>
      )}

      {sucesso && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start space-x-3">
          <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-sm font-medium text-green-900">Sucesso!</h4>
            <p className="text-sm text-green-700 mt-1">{sucesso}</p>
          </div>
        </div>
      )}

      {/* Formulário */}
      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-md p-8 space-y-6">
        {/* Campos obrigatórios */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
            Campos Obrigatórios
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Ordem Cliente */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ordem do Cliente *
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="number"
                  name="ordem_cliente"
                  value={formData.ordem_cliente}
                  onChange={handleChange}
                  required
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Ex: 1234"
                />
              </div>
            </div>

            {/* Código Cliente */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Código do Cliente *
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="number"
                  name="codigo_cliente"
                  value={formData.codigo_cliente}
                  onChange={handleChange}
                  required
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Ex: 5678"
                />
              </div>
            </div>

            {/* Nome Cliente */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome do Cliente *
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  name="nome_cliente"
                  value={formData.nome_cliente}
                  onChange={handleChange}
                  required
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Ex: João Silva"
                />
              </div>
            </div>

            {/* Valor */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Valor (R$) *
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  name="valor"
                  value={formData.valor}
                  onChange={handleValorChange}
                  required
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="0,00"
                />
              </div>
            </div>

            {/* CPF/CNPJ */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                CPF/CNPJ *
              </label>
              <div className="relative">
                <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  name="cpf_cnpj"
                  value={formData.cpf_cnpj}
                  onChange={handleChange}
                  required
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="000.000.000-00 ou 00.000.000/0000-00"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Campos opcionais */}
        <div className="pt-6 border-t border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <span className="w-2 h-2 bg-gray-400 rounded-full mr-2"></span>
            Campos Opcionais
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                E-mail
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="cliente@email.com"
                />
              </div>
            </div>

            {/* Telefone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Telefone
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="tel"
                  name="telefone"
                  value={formData.telefone}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="(85) 98765-4321"
                />
              </div>
            </div>

            {/* Data Vencimento */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data de Vencimento
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="date"
                  name="data_vencimento"
                  value={formData.data_vencimento}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Descrição */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descrição
              </label>
              <div className="relative">
                <FileText className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <textarea
                  name="descricao"
                  value={formData.descricao}
                  onChange={handleChange}
                  rows={3}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                  placeholder="Descrição do pagamento..."
                />
              </div>
            </div>
          </div>
        </div>

        {/* Botão Submit */}
        <div className="pt-6 border-t border-gray-200">
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold py-3 px-6 rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Gerando Link PIX...</span>
              </>
            ) : (
              <>
                <QrCode className="w-5 h-5" />
                <span>Gerar Link de Pagamento</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}